import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import { Button } from '@/src/elements/button/default'
import { Stack } from '@/src/elements/navigation/Stack'
import { Placeholder } from '@/src/elements/placeholder'
import { COMMUNITY_LIFETIME } from '@/src/features/community/models/community.constants'
import { useCommunityQuery } from '@/src/features/community/services/useCommunityQuery'
import { useNotificationSettingsQuery } from '@/src/features/notification/services/useNotificationSettingsQuery'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { routeToPath } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { SettingsCardCommunities } from '../../components/SettingsCardCommunities'
import { SettingsScreenWrapper } from '../../components/SettingsScreenWrapper'

export type TNotificationsCommunitySettingsScreenProps = { updateParametersToken: string | undefined } & TIdSlugParam

export const NotificationsCommunitySettingsScreen = ({
  idSlug,
  updateParametersToken,
}: TNotificationsCommunitySettingsScreenProps): ReactNode => {
  const { t } = useTranslate()
  const { navigate } = useRouter()
  const { routes } = useRoutes()
  const [communityId] = parseIdDash(idSlug)
  const { data: communityData } = useCommunityQuery(communityId)

  const { data, error } = useNotificationSettingsQuery(
    {
      updateParametersToken,
    },
    !!updateParametersToken,
  )

  if (error || !idSlug) return <ErrorMessage error={error} defaultErrorKey="community.load.error" />

  // Find the specific community's notification settings
  const communityNotificationSettings = data?.publications[communityId]
  const communityAnnouncementSettings = data?.announcements[communityId]

  if (data && (!communityNotificationSettings || !communityAnnouncementSettings)) {
    return <ErrorMessage defaultErrorKey="community.not-joined" />
  }

  return (
    <SettingsScreenWrapper testID={TEST_ID.SETTINGS.COMMUNITY_NOTIFICATIONS.SCREEN}>
      <Stack.Screen options={{ title: t('settings.header.title.community') }} />
      {communityNotificationSettings && communityAnnouncementSettings ? (
        <SettingsCardCommunities
          communityId={communityId}
          communityName={communityNotificationSettings.communityName}
          publications={communityNotificationSettings}
          announcements={communityAnnouncementSettings}
          updateParametersToken={updateParametersToken}
        />
      ) : (
        <Placeholder className="h-80" testID={TEST_ID.SETTINGS.COMMUNITY_NOTIFICATIONS.PLACEHOLDER} />
      )}
      <Button
        colors="primary"
        variant="light"
        contrast="default"
        size="md"
        onPress={() =>
          updateParametersToken
            ? navigate(routes.connect(routeToPath(routes.leaveCommunity(idSlug))))
            : navigate(routes.leaveCommunity(idSlug))
        }
      >
        <FormattedMessage
          id={
            communityData?.lifetime === COMMUNITY_LIFETIME.EVERLASTING
              ? 'community.leave-community'
              : 'community.toolbar.stop'
          }
          values={{ lifetime: communityData?.lifetime }}
        />
      </Button>
    </SettingsScreenWrapper>
  )
}
